import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatChipGrid, MatChipInput, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { map, startWith } from 'rxjs';
import { CompanyProductStatusMonitor } from 'src/app/models/company_product_status_monitor.model';
import { MessagingConversation } from 'src/app/models/messaging_conversation.model';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services/api.service';

@Component({
  selector: 'app-configure-status-monitor-notifiables-dialog',
  templateUrl: './configure-status-monitor-notifiables-dialog.component.html',
  styleUrls: ['./configure-status-monitor-notifiables-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButton,
    MatFormField,
    MatLabel,
    MatInput,
    MatChipGrid,
    MatChipRow,
    MatChipRemove,
    MatChipInput,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    MatIcon,
    ReactiveFormsModule,
    MatProgressSpinner,
  ],
})
export class ConfigureStatusMonitorNotifiablesDialogComponent {
  private readonly api = inject(ApiService);
  private readonly dialog_ref = inject(MatDialogRef<ConfigureStatusMonitorNotifiablesDialogComponent>);
  readonly data = inject<ConfigureStatusMonitorNotifiablesDialogData>(MAT_DIALOG_DATA);

  readonly formatUserLabel = formatUserLabel;
  readonly default_notifiables = this.data.default_notifiables;

  readonly saving = signal(false);
  private readonly notifiable_user_options = toSignal(
    this.api.getActiveCompanyProductMembersWithBusiness(String(this.data.company_product_id)).pipe(
      map(members => {
        const options: NotifiableUserOption[] = [];
        const seen_ids = new Set<number>();

        const push_user = (user: User | undefined, type: NotifiableUserType): void => {
          if (user == null || seen_ids.has(user.id)) {
            return;
          }

          seen_ids.add(user.id);
          options.push({
            id: user.id,
            full_name: formatUserLabel(user),
            type,
            type_label: NOTIFIABLE_USER_TYPE_LABELS[type],
          });
        };

        members.bisual_users.forEach(member => push_user(member.user, 'bisual'));
        members.company_users.forEach(user => push_user(user, 'cliente'));
        members.account_manager_users.forEach(user => push_user(user, 'partner'));

        return options;
      })
    ),
    { initialValue: [] as NotifiableUserOption[] }
  );

  private readonly conversation_options = toSignal(
    this.api
      .getMessagingConversations({
        company_id: this.data.company_id,
        per_page: 200,
        scopes: 'active',
      })
      .pipe(
        map(response =>
          response.data.map(
            (conversation): NotifiableConversationOption => ({
              ...conversation,
              provider_label: MESSAGING_PROVIDER_LABELS[conversation.provider],
            })
          )
        )
      ),
    { initialValue: [] as NotifiableConversationOption[] }
  );

  readonly filtered_users = computed((): NotifiableUserOption[] =>
    filterUsers(this.notifiable_user_options(), this.selected_users(), this.users_search_term())
  );

  readonly filtered_conversations = computed((): NotifiableConversationOption[] =>
    filterMessagingConversations(this.conversation_options(), this.selected_conversations(), this.conversations_search_term())
  );

  private readonly users_search_term = signal('');
  private readonly conversations_search_term = signal('');

  constructor() {
    this.users_search_ctrl.valueChanges
      .pipe(startWith(this.users_search_ctrl.value), takeUntilDestroyed())
      .subscribe(term => this.users_search_term.set(term));

    this.conversations_search_ctrl.valueChanges
      .pipe(startWith(this.conversations_search_ctrl.value), takeUntilDestroyed())
      .subscribe(term => this.conversations_search_term.set(term));
  }

  readonly selected_users = signal<User[]>([...(this.data.monitor.user_notifiables ?? [])]);
  readonly selected_conversations = signal<MessagingConversation[]>([...(this.data.monitor.messaging_conversations ?? [])]);

  readonly users_search_ctrl = new FormControl('', { nonNullable: true });
  readonly conversations_search_ctrl = new FormControl('', { nonNullable: true });

  addUser(user: NotifiableUserOption): void {
    const selected = this.selected_users();
    if (selected.some(existing => existing.id === user.id)) {
      return;
    }

    this.selected_users.set([
      ...selected,
      {
        id: user.id,
        full_name: user.full_name,
        name: user.full_name,
        surnames: '',
      } as User,
    ]);
    this.users_search_ctrl.setValue('');
  }

  removeUser(index: number): void {
    const selected = [...this.selected_users()];
    selected.splice(index, 1);
    this.selected_users.set(selected);
  }

  addConversation(conversation: NotifiableConversationOption): void {
    const selected = this.selected_conversations();
    if (selected.some(existing => existing.id === conversation.id)) {
      return;
    }

    this.selected_conversations.set([...selected, conversation]);
    this.conversations_search_ctrl.setValue('');
  }

  removeConversation(index: number): void {
    const selected = [...this.selected_conversations()];
    selected.splice(index, 1);
    this.selected_conversations.set(selected);
  }

  save(): void {
    if (this.saving()) {
      return;
    }

    this.saving.set(true);
    this.api
      .syncCompanyProductStatusMonitorNotifiables(this.data.monitor.id, {
        user_ids: this.selected_users().map(user => user.id),
        messaging_conversation_ids: this.selected_conversations().map(conversation => conversation.id),
      })
      .subscribe({
        next: (monitor: CompanyProductStatusMonitor): void => {
          this.saving.set(false);
          this.dialog_ref.close(monitor);
        },
        error: (): void => {
          this.saving.set(false);
        },
      });
  }

  cancel(): void {
    this.dialog_ref.close(undefined);
  }
}

export interface ConfigureStatusMonitorNotifiablesDialogData {
  monitor: CompanyProductStatusMonitor;
  company_product_id: number;
  company_id: number;
  default_notifiables: DefaultStatusMonitorNotifiable[];
}

export interface DefaultStatusMonitorNotifiable {
  kind: 'user' | 'conversation';
  id: number;
  label: string;
  reason_label: string;
  bot_is_member?: boolean;
}

interface NotifiableUserOption {
  id: number;
  full_name: string;
  type: NotifiableUserType;
  type_label: string;
}

interface NotifiableConversationOption extends MessagingConversation {
  provider_label: string;
}

type NotifiableUserType = 'bisual' | 'cliente' | 'partner';

const NOTIFIABLE_USER_TYPE_LABELS: Record<NotifiableUserType, string> = {
  bisual: 'Bisual',
  cliente: 'Cliente',
  partner: 'Partner',
};

const MESSAGING_PROVIDER_LABELS: Record<MessagingConversation['provider'], string> = {
  slack: 'Slack',
  google_chat: 'Google Chat',
  teams: 'Teams',
};

function filterUsers(options: NotifiableUserOption[], selected: User[], term: string): NotifiableUserOption[] {
  const normalized_term = term.trim().toLowerCase();
  const selected_ids = new Set(selected.map(user => user.id));

  return options.filter(user => {
    if (selected_ids.has(user.id)) {
      return false;
    }

    if (normalized_term === '') {
      return true;
    }

    const label = user.full_name.toLowerCase();
    return label.includes(normalized_term);
  });
}

function filterMessagingConversations(
  options: NotifiableConversationOption[],
  selected: MessagingConversation[],
  term: string
): NotifiableConversationOption[] {
  const normalized_term = term.trim().toLowerCase();
  const selected_ids = new Set(selected.map(conversation => conversation.id));

  return options.filter(conversation => {
    if (selected_ids.has(conversation.id)) {
      return false;
    }

    if (normalized_term === '') {
      return true;
    }

    return (
      conversation.display_name.toLowerCase().includes(normalized_term) ||
      conversation.provider_label.toLowerCase().includes(normalized_term)
    );
  });
}

export function formatUserLabel(user: User): string {
  if (user.full_name != null && user.full_name !== '') {
    return user.full_name;
  }

  return `${user.name} ${user.surnames}`.trim();
}

export function formatMessagingConversationLabel(conversation: MessagingConversation): string {
  return `${conversation.display_name} (${MESSAGING_PROVIDER_LABELS[conversation.provider]})`;
}
